import React, { useEffect, useRef } from "react";
import { Bot, Workflow, FileSpreadsheet, GitBranch, Cpu, Cloud, Network, ScrollText, Wand2 } from "lucide-react";

const ITEMS = [
  { label: "UiPath", icon: Bot },
  { label: "Power Automate", icon: Workflow },
  { label: "Power Automate Desktop", icon: Cpu },
  { label: "Excel Automation", icon: FileSpreadsheet },
  { label: "RPA", icon: GitBranch },
  { label: "Workflow Design", icon: Network },
  { label: "Cloud Flows", icon: Cloud },
  { label: "SharePoint", icon: ScrollText },
  { label: "AI Builder", icon: Wand2 },
];

const AUTO_SPEED = 0.55; // px/frame
const REPEATS = 4;
const RESUME_MS = 1800; // auto-scroll resumes after this idle period

export function TechStack({ t }) {
  const scrollerRef = useRef(null);
  const halfWidthRef = useRef(0);
  const rafRef = useRef(0);
  const userActiveRef = useRef(false);
  const idleTimerRef = useRef(null);

  // Mouse-drag-only state (desktop). Touch is handled by native overflow scrolling.
  const dragRef = useRef({ active: false, startX: 0, startScroll: 0 });

  // Measure once mounted and on resize so we can loop seamlessly.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const measure = () => {
      halfWidthRef.current = el.scrollWidth / REPEATS;
      if (el.scrollLeft === 0) el.scrollLeft = halfWidthRef.current;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // Auto-scroll loop.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const tick = () => {
      const half = halfWidthRef.current || 1;

      if (!userActiveRef.current && !dragRef.current.active) {
        el.scrollLeft += AUTO_SPEED;
      }

      // Seamless wrap in either direction
      if (el.scrollLeft >= half * 2) el.scrollLeft -= half;
      else if (el.scrollLeft < half) el.scrollLeft += half;

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const markUserActive = () => {
    userActiveRef.current = true;
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      userActiveRef.current = false;
    }, RESUME_MS);
  };

  // ─── Desktop mouse drag (touch is intentionally NOT handled here) ─────────
  const onMouseDown = (e) => {
    const el = scrollerRef.current;
    if (!el) return;
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startScroll: el.scrollLeft,
    };
    el.style.cursor = "grabbing";
    markUserActive();
    e.preventDefault(); // stop text selection
  };

  // Global mouse listeners so drag continues even when cursor leaves the scroller.
  // All logic is inline to keep the useEffect dependency array clean.
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.active) return;
      const el = scrollerRef.current;
      if (!el) return;
      const dx = e.clientX - dragRef.current.startX;
      el.scrollLeft = dragRef.current.startScroll - dx;
      userActiveRef.current = true;
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        userActiveRef.current = false;
      }, RESUME_MS);
    };
    const onUp = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;
      const el = scrollerRef.current;
      if (el) el.style.cursor = "grab";
      userActiveRef.current = true;
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        userActiveRef.current = false;
      }, RESUME_MS);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  return (
    <section id="stack" className="relative section-pad border-y border-white/10 bg-[#0E162F]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 mb-10 md:mb-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-8 h-px bg-[#00E5FF]" />
              <span className="eyebrow">{t.stack.eyebrow}</span>
            </div>
            <h2
              className="text-white font-semibold leading-[1.05] tracking-tight"
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: "clamp(28px, 4vw, 44px)",
              }}
            >
              {t.stack.heading}
            </h2>
          </div>
        </div>
      </div>

      <div className="marquee-mask">
        <div
          ref={scrollerRef}
          onMouseDown={onMouseDown}
          onTouchStart={markUserActive}
          onWheel={markUserActive}
          className="flex items-center overflow-x-scroll no-scrollbar py-2 select-none"
          style={{
            cursor: "grab",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            touchAction: "pan-x pan-y", // allow both directions; native touch scrolls horizontally
            WebkitOverflowScrolling: "touch",
            overscrollBehaviorX: "contain",
          }}
          data-testid="stack-marquee"
        >
          {Array.from({ length: REPEATS }).flatMap((_, copy) =>
            ITEMS.map((it, i) => {
              const Icon = it.icon;
              return (
                <div
                  key={`${copy}-${i}`}
                  data-testid={`stack-badge-${copy}-${i}`}
                  className="mx-3 my-2 shrink-0 inline-flex items-center gap-3 border border-white/10 rounded-full px-6 py-3 bg-[#1C2331] hover:border-[#00E5FF]/50 transition-colors duration-300"
                  draggable={false}
                  style={{ pointerEvents: "none" }}
                >
                  <Icon size={16} className="text-[#00E5FF]" />
                  <span
                    className="text-white text-sm font-bold whitespace-nowrap"
                    style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: "0.02em" }}
                  >
                    {it.label}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
}
